import merge from 'lodash/merge';
import { SourceType } from '../helpers/home_services.helper';
import { UserChatAnswers } from '../chat/answers/chat_answers';

export interface UserSessionData {
  readonly publicId: string;
  readonly answerAttrs?: UserChatAnswers;
  readonly sourceType?: SourceType;
  readonly sourceId?: string;
  readonly sourceName?: string;
  readonly campaignId?: string;
  readonly zipCodeByIp?: string;
}

export enum SessionStorageType {
  STRING = 'string',
  OBJECT = 'object',
}

export enum SessionStorageKey {
  staticResults = 'staticResults',
  results = 'results',
  userData = 'userData',
  jornayaToken = 'jornayaToken',
  userPublicId = 'userPublicId',
}

export const setSessionStorageItem = <TValue extends object | string>(
  key: SessionStorageKey,
  value: TValue
): void => {
  if (key == null || value == null) return;

  try {
    let resultToSave: string;
    const type =
      typeof value === 'object'
        ? SessionStorageType.OBJECT
        : SessionStorageType.STRING;
    switch (type) {
      case SessionStorageType.STRING:
        resultToSave = value as string;
        break;
      case SessionStorageType.OBJECT:
        resultToSave = JSON.stringify(value);
        break;
    }
    sessionStorage.setItem(key, resultToSave);
  } catch (error) {
    console.log('localforage set item error:', error);
  }
};

export const getSessionStorageItem = <TValue extends object | string>(
  key: SessionStorageKey,
  type: SessionStorageType = SessionStorageType.STRING
): TValue | undefined => {
  if (key == null) return undefined;

  try {
    const value = sessionStorage.getItem(key);
    if (value == null) return undefined;

    switch (type) {
      case SessionStorageType.STRING:
        return value as TValue;
      case SessionStorageType.OBJECT:
        return JSON.parse(value);
    }
  } catch (error) {
    console.log('sessionStorage error:', error);
  }
  return undefined;
};

export const updateSessionObjectStorageItem = <TValue extends object>(
  key: SessionStorageKey,
  value: Partial<TValue>
): void => {
  const existingValue = getSessionStorageItem<TValue>(
    key,
    typeof value === 'object'
      ? SessionStorageType.OBJECT
      : SessionStorageType.STRING
  );
  const newValue = merge(existingValue, value);
  setSessionStorageItem(key, newValue);
};

export const deleteSessionStorageItem = (key: SessionStorageKey): void => {
  if (key == null) return;

  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.log('sessionStorage error:', error);
  }
};

export const getStringSessionStorageItem = (
  key: SessionStorageKey
): string | undefined => {
  return getSessionStorageItem(key, SessionStorageType.STRING) as
    | string
    | undefined;
};

export const getObjectSessionStorageItem = <TObject>(
  key: SessionStorageKey
): TObject | undefined => {
  return getSessionStorageItem(key, SessionStorageType.OBJECT) as
    | TObject
    | undefined;
};
